import type { ReactNode } from "react";

import { Button } from "@mui/material";
import { SnackbarProvider, useSnackbar, type VariantType } from "notistack";

interface SnackProviderProps {
	children: ReactNode;
}

/** Wraps the app in notistack's SnackbarProvider — the single snackbar host for the app. */
const SnackProvider = ({ children }: SnackProviderProps) => (
	<SnackbarProvider dense anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
		{children}
	</SnackbarProvider>
);

export default SnackProvider;

type CloseSnack = (key: string | number) => void;
type OpenSnack = (msg: ReactNode, variant?: VariantType, persist?: boolean) => string | number;

/** Hook for queuing snackbars from components — use this instead of MUI Alert. */
export const useSnacks = (): [OpenSnack, CloseSnack] => {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	const openSnack: OpenSnack = (msg, variant = "default", persist = false) => {
		const action = persist
			? (key: string | number) => (
					<Button color="inherit" variant="outlined" onClick={() => closeSnackbar(key)}>
						X
					</Button>
				)
			: undefined;

		return enqueueSnackbar(msg, { variant, persist, action });
	};

	const closeSnack: CloseSnack = (key) => closeSnackbar(key);

	return [openSnack, closeSnack];
};
